import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Grid,
    IconButton,
    Modal,
    Stack,
    TableCell,
    Typography,
} from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import React, { useEffect, useState, FocusEvent } from 'react';
import { Path, UseFormGetValues, useForm } from 'react-hook-form';
import { typeNumberPhoneFind } from '../../communs/enums/typeNumberPhone';
import { extraValidationCNPJ } from '../../communs/validations/cnpj';
import { extraValidationCPF } from '../../communs/validations/cpf';
import { FormButton } from '../../componets/form/formButton';
import { cepMask } from '../../componets/form/mask/cep';
import { phoneMask } from '../../componets/form/mask/phone';
import { useContextGlobal } from '../../context/ContextGlobal';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import { handleExceptionMessage } from '../../util/handleExceptionAxios';
import { message } from '../../util/handleMessages';
import { FormInputProps } from '../../componets/form/formInterfaces';
import { ColumnDef } from '@tanstack/react-table';
import { IconComponent, icons } from '../../componets/icons';
import RenderForm from '../../componets/form/renderForm';
import { useWindowSize } from '../../util/responsiveness';
import FormComplete from '../../componets/form/formComplete';
import {
    ImperativeHandleAccessedByFormCustomer,
    ImperativeHandleAccessedByParentComponent,
} from '../../componets/interfaces/formProps';
import * as ImplEnum from '../../communs/enums/generic-enum';

interface IFormInput {
    name: string;
    identification: string;
    birthDate: Date | null;
    formattedBirthDate: string | null;
    email: string;
    phones: IPhone[];
    publicPlace: string;
    number: string;
    addressComplement: string;
    district?: {
        id: string;
        description: string;
        cityIbgeId?: string;
        cityName?: string;
        cityAutocomplete?: {
            id: string;
            description: string;
        };
        stateIbgeId?: string;
        state?: string;
        stateName?: string;
        stateAutocomplete?: {
            id: string;
            description: string;
        };
        cep?: string;
    };
}

interface DataApiViaCep {
    cep: string;
    logradouro?: string;
    complemento?: string;
    bairro?: string;
    localidade: string;
    uf: string;
    ibge: string;
    gia: string;
    ddd: string;
    siafi: string;
    erro?: boolean;
}

interface IPhone {
    phoneType: string;
    phoneDdd: string;
    phoneNumber: string;
    branch: string;
    id?: string;
    phoneTypeOption?: {
        id: string;
        description: string;
    };
}

interface ModalDistrictProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    width: number;
    getValues?: UseFormGetValues<any>;
    setValue?: any;
}

interface IFormCustomer {
    settings: any;
    actionByModal?: boolean;
    callbackSuccessByModal?: (response: any) => void;
    params?: {
        id: string;
    };
}

const defaultValues = {
    name: '',
    identification: '',
    email: '',
    publicPlace: '',
    number: '',
    birthDate: null,
    formattedBirthDate: null,
};
const enumInstances = {
    howDidYouFindOptionsInstance: new ImplEnum.GenericEnum(
        ImplEnum.howDidYouFindOptions,
    ),
    yesOrNoOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.yesOrNoOptions),
    typeNumberPhoneInstance: new ImplEnum.GenericEnum(ImplEnum.typeNumberPhone),
    statusOptionsInstance: new ImplEnum.GenericEnum(ImplEnum.statusOptions),
};

const typeNumberPhone = enumInstances.typeNumberPhoneInstance.optionsList();

const Form = React.forwardRef(
    (
        props: IFormCustomer,
        ref?: React.Ref<ImperativeHandleAccessedByFormCustomer<IFormInput>>,
    ) => {
        const { settings, actionByModal, callbackSuccessByModal, params } =
            props;

        const { addToast } = useToast();
        const { setOpenLoading } = useContextGlobal();
        const [expanded, setExpanded] = React.useState<'panel-address' | false>(
            false,
        );

        const refForm =
            React.useRef<ImperativeHandleAccessedByParentComponent<IFormInput>>(
                null,
            );

        const [optionsDistrict, setOptionsDistrict] = useState([]);
        const [optionsCity, setOptionsCity] = useState([]);
        const [optionsState, setOptionsState] = useState<
            { id: string; description: string }[]
        >([]);
        const [width] = useWindowSize();
        const [openModalDistrict, setOpenModalDistrict] = useState(false);

        const nameFields = 'phones';

        useEffect(() => {
            if (optionsState.length === 0 && expanded === 'panel-address') {
                updateState();

                if (refForm?.current?.getValues('district.cityIbgeId')) {
                    api.get(
                        `district?district.cityIbgeId=${refForm?.current?.getValues(
                            'district.cityIbgeId',
                        )}`,
                    ).then(response => {
                        setOptionsDistrict(response.data.data);
                    });
                }

                if (refForm?.current?.getValues('district.stateIbgeId')) {
                    updateCity(
                        '' +
                            refForm?.current?.getValues('district.stateIbgeId'),
                    );
                }
            }
        }, [expanded, optionsState.length]);

        const columnsTable = React.useMemo<ColumnDef<IPhone>[]>(
            () => [
                {
                    id: 'phoneType',
                    header: () => (
                        <TableCell align={'left'} padding={'normal'}>
                            Tipo
                        </TableCell>
                    ),
                    cell: ({ row }: any) => {
                        return (
                            <TableCell
                                align="left"
                                key={row.id}
                                onClick={() =>
                                    refForm?.current?.handleClick(
                                        row.id,
                                        nameFields,
                                    )
                                }>
                                {
                                    typeNumberPhoneFind(row.original.phoneType)
                                        ?.description
                                }
                            </TableCell>
                        );
                    },
                },
                {
                    accessorKey: 'phoneDdd',
                    header: () => (
                        <TableCell align={'left'} padding={'normal'}>
                            DDD
                        </TableCell>
                    ),
                    cell: (cell: any) => {
                        return (
                            <TableCell
                                align="left"
                                onClick={() =>
                                    refForm?.current?.handleClick(
                                        cell.row.id,
                                        nameFields,
                                    )
                                }>
                                {cell.row.original.phoneDdd}
                            </TableCell>
                        );
                    },
                },
                {
                    id: 'user',
                    header: (info: any) => {
                        return (
                            <TableCell align={'left'} padding={'normal'}>
                                Número
                            </TableCell>
                        );
                    },
                    cell: ({ row }: any) => {
                        return (
                            <TableCell
                                align="left"
                                key={row.id}
                                onClick={() =>
                                    refForm?.current?.handleClick(
                                        row.id,
                                        nameFields,
                                    )
                                }>
                                {phoneMask(row.original.phoneNumber, false)}
                            </TableCell>
                        );
                    },
                },
                {
                    id: 'user',
                    header: (info: any) => {
                        return (
                            <TableCell align={'left'} padding={'normal'}>
                                Ramal
                            </TableCell>
                        );
                    },
                    cell: ({ row }: any) => {
                        return (
                            <TableCell
                                align="left"
                                key={row.id}
                                onClick={() =>
                                    refForm?.current?.handleClick(
                                        row.id,
                                        nameFields,
                                    )
                                }>
                                {row.original?.branch || ''}
                            </TableCell>
                        );
                    },
                },
                {
                    id: 'select',
                    header: ({ table }: any) => <TableCell align="center" />,
                    cell: ({ row }: any) => (
                        <TableCell align="right">
                            <IconButton
                                onClick={() =>
                                    refForm?.current?.handleClick(
                                        row.id,
                                        nameFields,
                                    )
                                }>
                                <IconComponent icon={icons.ModeEdit} />
                            </IconButton>
                            <IconButton
                                onClick={() => {
                                    refForm?.current?.handleDeleteItemTable(
                                        row.id,
                                        nameFields,
                                    );
                                }}>
                                <IconComponent icon={icons.Delete} />
                            </IconButton>
                        </TableCell>
                    ),
                },
            ],
            [],
        );

        const handleChangeAccordion =
            (panel: 'panel-address') =>
            (event: React.SyntheticEvent, isExpanded: boolean) => {
                setExpanded(isExpanded ? panel : false);
            };

        const handleOnBlurCep = React.useCallback(
            async (event: FocusEvent<HTMLInputElement>) => {
                let value = event.target.value;
                if (value) {
                    value = value.replace(/\D/g, '');
                }

                if (!value) {
                    return;
                }

                setOpenLoading(true);
                try {
                    const response: AxiosResponse<DataApiViaCep> =
                        await axios.get(
                            `https://viacep.com.br/ws/${value}/json/`,
                        );

                    const data = response.data;

                    if (data.erro) {
                        addToast({
                            type: 'warn',
                            title: message.error.selectOne,
                            description:
                                'O CEP deve estar inválido, verifique e digite novamente',
                        });
                    }

                    if (data.bairro) {
                        // API que busca district pela descrição, se não achar no banco ja cadastra ele e devolve, agora se achar, apenas devolve o registro
                        try {
                            const resp = await api.get(
                                `district/exist-district?description=${data.bairro}&cityIbgeId=${data.ibge}&cityName=${data.localidade}&state=${data.uf}&cep=${data.cep}`,
                            );

                            refForm?.current?.setValue('district', {
                                id: resp.data.id,
                                description: resp.data.description,
                            });
                        } catch (error) {
                            addToast({
                                type: 'error',
                                title: message.error.save,
                                description:
                                    'Tente salvar o bairro manualmente',
                            });
                            console.error(error);
                        }
                    } else {
                        try {
                            const response = await api.get(
                                `district?district.cityIbgeId=${data.ibge}`,
                            );
                            setOptionsDistrict(response.data.data);
                        } catch (error) {
                            addToast({
                                type: 'error',
                                title: message.error.selectAll,
                                description:
                                    'Não foi possível selecionar os bairros',
                            });
                            console.error(error);
                        }
                        refForm?.current?.setValue('district', undefined);
                    }

                    refForm?.current?.setValue(
                        'publicPlace',
                        data?.logradouro || '',
                    );
                    refForm?.current?.setValue(
                        'district.cityName',
                        data.localidade,
                    );
                    refForm?.current?.setValue(
                        'district.cityIbgeId',
                        data.ibge,
                    );
                    refForm?.current?.setValue('district.cityAutocomplete', {
                        id: data.ibge,
                        description: data.localidade,
                    });

                    const dataState = await findStateBySigla(data.uf);
                    updateCity(dataState.id);
                    refForm?.current?.setValue('district.state', data.uf);
                    refForm?.current?.setValue(
                        'district.stateIbgeId',
                        dataState.id,
                    );
                    refForm?.current?.setValue(
                        'district.stateName',
                        dataState.nome,
                    );
                    refForm?.current?.setValue('district.stateAutocomplete', {
                        id: dataState.id,
                        description: `${data.uf} - ${dataState.nome}`,
                    });
                } catch (error) {
                    addToast({
                        type: 'warn',
                        title: message.error.selectOne,
                        description:
                            'O CEP deve estar inválido, verifique e digite novamente',
                    });
                }
                setOpenLoading(false);
            },
            [],
        );

        const updateCity = async (idState: string) => {
            const response = await axios.get(
                `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${idState}/municipios`,
            );

            const data = response.data
                ? response.data.map((d: any) => {
                      return {
                          id: d.id,
                          description: d.nome,
                      };
                  })
                : [];
            setOptionsCity(data);
            return data;
        };

        const onChangeState = React.useCallback(
            async (event: React.ChangeEvent<HTMLInputElement>, option: any) => {
                const [uf, nome] = option.description.split(' - ');
                refForm?.current?.setValue('district.state', uf);
                refForm?.current?.setValue('district.stateName', nome);
                refForm?.current?.setValue('district.stateIbgeId', option.id);
                await updateCity(option.id);
            },
            [],
        );

        const onChangeCity = React.useCallback(
            async (event: React.ChangeEvent<HTMLInputElement>, option: any) => {
                if (option && option.id) {
                    const response = await api.get(
                        `district?district.cityIbgeId=${option.id}`,
                    );
                    setOptionsDistrict(response.data.data);
                    refForm?.current?.setValue(
                        'district.cityName',
                        option.description,
                    );
                    refForm?.current?.setValue(
                        'district.cityIbgeId',
                        option.id,
                    );
                }
            },
            [],
        );

        const inputsFormAccordion = React.useMemo<FormInputProps<IFormInput>[]>(
            () => [
                {
                    typeInput: 'text',
                    name: 'district.cep',
                    control: null,
                    label: 'CEP',
                    md: 2,
                    xs: 12,
                    toolTipPros: {
                        title: 'Digite o CEP para que a busca do endereço possa ser feita',
                        arrow: true,
                    },
                    mask: 'cep',
                    rules: {
                        required: true,
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo CEP é obrigatório',
                        },
                    ],
                    handleOnBlur: handleOnBlurCep,
                    noRender: true,
                },
                {
                    typeInput: 'autocomplete',
                    name: 'district.stateAutocomplete',
                    control: null,
                    label: 'Estado',
                    md: 3,
                    xs: 12,
                    options: optionsState,
                    loadingAutocomplete: false,
                    rules: {
                        required: true,
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo estado é obrigatório',
                        },
                    ],
                    handleChange: onChangeState,
                    activeDebounce: true,
                    noRender: true,
                    readOnly: true,
                },
                {
                    typeInput: 'autocomplete',
                    name: 'district.cityAutocomplete',
                    control: null,
                    label: 'Cidade',
                    md: 3,
                    xs: 12,
                    options: optionsCity,
                    loadingAutocomplete: false,
                    rules: {
                        required: true,
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo cidade é obrigatório',
                        },
                    ],
                    handleChange: onChangeCity,
                    activeDebounce: true,
                    noRender: true,
                    readOnly: true,
                },
                {
                    typeInput: 'autocomplete',
                    name: 'district',
                    control: null,
                    label: 'Bairro',
                    loadingAutocomplete: false,
                    options: optionsDistrict,
                    md: 4,
                    xs: 12,
                    rules: {
                        required: true,
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo bairro é obrigatório',
                        },
                    ],
                    actionOnInput: {
                        action: () => {
                            setOpenModalDistrict(true);
                        },
                        iconProps: {
                            icon: icons.AddCircle,
                        },
                        textTooltip: 'Adicionar bairro',
                    },
                    activeDebounce: true,
                    noRender: true,
                },
                {
                    typeInput: 'text',
                    name: 'publicPlace',
                    control: null,
                    label: 'Logradouro',
                    md: 5,
                    xs: 12,
                    rules: {
                        required: false,
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo logradouro é obrigatório',
                        },
                    ],
                    noRender: true,
                },
                {
                    typeInput: 'text',
                    name: 'addressComplement',
                    control: null,
                    label: 'Complemento',
                    md: 3,
                    xs: 12,
                    noRender: true,
                },
                {
                    typeInput: 'text',
                    name: 'number',
                    control: null,
                    label: 'Número',
                    md: 2,
                    xs: 12,
                    noRender: true,
                },
            ],
            [
                handleOnBlurCep,
                onChangeCity,
                onChangeState,
                optionsCity,
                optionsDistrict,
                optionsState,
            ],
        );

        const inputsForm = React.useMemo<FormInputProps<IFormInput>[]>(
            () => [
                {
                    typeInput: 'text',
                    name: 'name',
                    control: null,
                    label: 'Nome',
                    md: 6,
                    xs: 12,
                    rules: {
                        required: true,
                        minLength: 5,
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo nome é obrigatório',
                        },
                        {
                            type: 'minLength',
                            message:
                                'O campo nome tem que ter mais do que 5 letras',
                        },
                    ],
                },
                {
                    typeInput: 'text',
                    name: 'identification',
                    control: null,
                    label: 'Identificação (CPF/CNPJ)',
                    md: 6,
                    xs: 12,
                    mask: 'identification',
                    rules: {
                        required: true,
                        validate: {
                            validateCNPJ: extraValidationCNPJ,
                            validateCPF: extraValidationCPF,
                        },
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo identificação é obrigatório',
                        },
                        {
                            type: 'validateCNPJ',
                            message: 'CNPJ inválido',
                        },
                        {
                            type: 'validateCPF',
                            message: 'CPF inválido',
                        },
                    ],
                },
                {
                    typeInput: 'date',
                    name: 'birthDate',
                    control: null,
                    label: 'Data de nascimento',
                    md: 6,
                    xs: 12,
                    placeholder: 'dd/mm/aaaa',
                    inputFormat: 'dd/MM/yyyy',
                    rules: {
                        validate: (value: any) => {
                            if (value && String(value) === 'Invalid Date') {
                                return 'A data está em um formato inválido';
                            }
                        },
                        required: false,
                    },
                    messagesError: [
                        {
                            type: 'required',
                            message: 'O campo data de nascimento é obrigatório',
                        },
                    ],
                },
                {
                    typeInput: 'text',
                    name: 'email',
                    control: null,
                    label: 'Email',
                    md: 6,
                    xs: 12,
                    rules: {
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    },
                    messagesError: [
                        {
                            type: 'pattern',
                            message:
                                'O email está inválido. Ex.: email@email.com',
                        },
                    ],
                },
                {
                    typeInput: 'subForm',
                    name: nameFields,
                    control: null,
                    label: 'Telefones',
                    md: 12,
                    xs: 12,
                    dto: data => {
                        if (
                            !data[nameFields] ||
                            Array.isArray(
                                data[nameFields] &&
                                    data[nameFields].length === 0,
                            )
                        ) {
                            delete data[nameFields];
                        }
                        return data;
                    },
                    dataTable: {
                        propsTable: {
                            columns: columnsTable,
                            data: [],
                            minWidth: 600,
                        },
                        inputs: [
                            {
                                typeInput: 'autocomplete',
                                name: 'phoneType',
                                control: null,
                                label: 'Tipo',
                                loadingAutocomplete: false,
                                setValue: null,
                                options: typeNumberPhone,
                                md: 5,
                                xs: 12,
                                rules: {
                                    required: true,
                                },
                                messagesError: [
                                    {
                                        type: 'required',
                                        message: 'O campo tipo é obrigatório',
                                    },
                                ],
                                dto: data => {
                                    data.phoneType = data.phoneType.id;
                                    return data;
                                },
                                setData: data => {
                                    return enumInstances.typeNumberPhoneInstance.getObject(
                                        data.phoneType,
                                    );
                                },
                            },
                            {
                                typeInput: 'text',
                                name: 'phoneDdd',
                                control: null,
                                label: 'DDD',
                                md: 2,
                                xs: 12,
                                mask: 'number',
                                rules: {
                                    required: true,
                                    maxLength: 3,
                                },
                                messagesError: [
                                    {
                                        type: 'required',
                                        message: 'O campo DDD é obrigatório',
                                    },
                                    {
                                        type: 'maxLength',
                                        message: 'Limite de número é 3',
                                    },
                                ],
                            },
                            {
                                typeInput: 'text',
                                name: 'phoneNumber',
                                control: null,
                                label: 'Número',
                                md: 3,
                                xs: 12,
                                mask: 'phone',
                                rules: {
                                    required: true,
                                },
                                messagesError: [
                                    {
                                        type: 'required',
                                        message: 'O campo Número é obrigatório',
                                    },
                                ],
                            },
                            {
                                typeInput: 'text',
                                name: 'branch',
                                control: null,
                                label: 'Ramal',
                                md: 2,
                                xs: 12,
                            },
                        ] as FormInputProps<IPhone>[],
                    },
                },
                {
                    typeInput: 'component',
                    name: 'accordion',
                    control: null,
                    label: '',
                    md: 12,
                    xs: 12,
                    componentProps: {
                        inputs: inputsFormAccordion,
                        componentDataInputs: inputs => {
                            return (
                                <Accordion
                                    expanded={expanded === 'panel-address'}
                                    onChange={handleChangeAccordion(
                                        'panel-address',
                                    )}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header">
                                        <Typography>Endereço</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <RenderForm inputsForm={inputs} />
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        },
                        conditionRender: true,
                    },
                },
            ],
            [columnsTable, expanded, inputsFormAccordion],
        );

        const validation = (data: any) => {
            if (!data.phones || data.phones.length < 1) {
                addToast({
                    type: 'warn',
                    title: message.warn.save,
                    description: 'Tem que ter ao menos um número para contato.',
                });
                return false;
            }
            return true;
        };

        const updateState = async () => {
            const response = await axios.get(
                `https://servicodados.ibge.gov.br/api/v1/localidades/estados`,
            );

            const data = response.data
                ? response.data.map((d: any) => {
                      return {
                          id: d.id,
                          description: d.nome,
                          initials: d.sigla,
                      };
                  })
                : [];

            setOptionsState(data);
        };

        const findStateBySigla = async (sigla: string) => {
            const response = await axios.get(
                `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${sigla}`,
            );

            return {
                id: response.data.id,
                sigla: response.data.sigla,
                nome: response.data.nome,
            };
        };

        const implSetValueData = React.useCallback(
            async (data: any) => {
                for (const input of inputsForm) {
                    if (input?.setData) {
                        refForm?.current?.setValue(
                            input.name as Path<any>,
                            input.setData(data),
                        );
                        continue;
                    }

                    refForm?.current?.setValue(
                        input.name as Path<any>,
                        data[input.name],
                    );
                }

                for (const input of inputsFormAccordion) {
                    if (input?.setData) {
                        refForm?.current?.setValue(
                            input.name as Path<any>,
                            input.setData(data),
                        );
                        continue;
                    }

                    refForm?.current?.setValue(
                        input.name as Path<any>,
                        data[input.name],
                    );
                }

                if (!data?.district?.id) {
                    return;
                }
                refForm?.current?.setValue(
                    'district.cep',
                    cepMask(data.district.cep, false),
                );
                refForm?.current?.setValue(
                    'district.cityName',
                    data.district.cityName,
                );
                refForm?.current?.setValue(
                    'district.cityIbgeId',
                    data.district.cityIbgeId,
                );
                refForm?.current?.setValue('district.cityAutocomplete', {
                    id: data.district?.cityIbgeId || '',
                    description: data.district?.cityName || '',
                });

                const dataState = await findStateBySigla(data.uf);
                updateCity(dataState.id);
                refForm?.current?.setValue(
                    'district.state',
                    data.district.state,
                );
                refForm?.current?.setValue(
                    'district.stateIbgeId',
                    data.district.stateIbgeId,
                );
                refForm?.current?.setValue(
                    'district.stateName',
                    data.district.stateName,
                );
                refForm?.current?.setValue('district.stateAutocomplete', {
                    id: data.district.stateIbgeId,
                    description: `${data.district.state} - ${data.district.stateName}`,
                });
            },
            [inputsForm, inputsFormAccordion],
        );

        const getValues = React.useCallback((key: any) => {
            return refForm?.current?.getValues(key);
        }, []);

        React.useImperativeHandle(
            ref,
            () => ({
                getValues,
            }),
            [getValues],
        );

        return (
            <>
                <ModalDistrictType
                    open={openModalDistrict}
                    setOpen={setOpenModalDistrict}
                    width={width}
                    getValues={refForm?.current?.getValues}
                    setValue={refForm?.current?.setValue}
                />
                <FormComplete
                    defaultValues={defaultValues}
                    inputsForm={[...inputsForm, ...inputsFormAccordion]}
                    ref={refForm}
                    settings={settings}
                    validation={validation}
                    implSetValueData={implSetValueData}
                    callbackSuccess={callbackSuccessByModal}
                    additionalDataQaSubmitButton={
                        actionByModal ? 'Customer' : ''
                    }
                    paramsComponent={params}
                />
            </>
        );
    },
);

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '800px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 2,
    borderRadius: '4px',
};

function ModalDistrictType({
    open,
    setOpen,
    width,
    getValues,
    setValue,
}: ModalDistrictProps) {
    const { addToast } = useToast();
    const { setOpenLoading } = useContextGlobal();

    const defaultValues = {
        description: '',
    };

    const styleModal = {
        ...style,
    };

    if (width <= 800) {
        styleModal.width = '90%';
    }

    const useFormModal = useForm<any>({
        defaultValues: defaultValues,
    });
    const controlItems = useFormModal.control;
    const handleSubmit = useFormModal.handleSubmit;
    const reset = useFormModal.reset;

    const inputsForm: FormInputProps<any>[] = [
        {
            typeInput: 'text',
            name: 'description',
            control: controlItems,
            label: 'Descrição',
            loadingAutocomplete: false,
            md: 12,
            xs: 12,
            rules: {
                required: true,
            },
            messagesError: [
                {
                    type: 'required',
                    message: 'O campo descrição é obrigatório',
                },
            ],
            autoFocus: true,
        },
    ];

    const handleClose = () => {
        setOpen(false);
        reset(defaultValues);
    };

    async function submitItem(data: any) {
        try {
            if (
                !getValues ||
                !getValues('district.state') ||
                !getValues('district.cityName')
            ) {
                addToast({
                    type: 'warn',
                    title: 'Preencha o formulário na sequência',
                    description:
                        'Na seguinte ordem: estado, cidade e em seguida salve o novo bairro',
                });
                return;
            }

            setOpenLoading(true);
            const district = getValues('district');
            if (district) {
                data = {
                    ...data,
                    cityIbgeId: district.cityIbgeId,
                    cityName: district.cityName,
                    stateIbgeId: district.stateIbgeId,
                    state: district.state,
                    stateName: district.stateName,
                    cep: district.cep?.replace('-', ''),
                };
            }
            const response = await api.post('district', data);

            setValue('district', response.data);
            addToast({
                type: 'success',
                title: message.success.save,
                description: 'Bairro salvo',
            });
            handleClose();
        } catch (error: any) {
            const messageResponse = handleExceptionMessage(error);
            addToast({
                type: 'error',
                title: message.error.save,
                description: messageResponse,
            });
        }
        setOpenLoading(false);
    }

    return (
        <div>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description">
                <Box sx={styleModal} component={'div'}>
                    <Typography
                        id="modal-modal-title"
                        variant="h6"
                        component="h2">
                        Adicionar bairro
                    </Typography>
                    <form onSubmit={handleSubmit(data => submitItem(data))}>
                        <Grid sx={{ pt: 2, pb: 2 }} container spacing={2}>
                            <RenderForm inputsForm={inputsForm} />
                        </Grid>
                        <span />
                        <Stack spacing={1} direction="row">
                            <FormButton
                                label={'Salvar e aplicar'}
                                typeButton={'submit'}
                                additionalDataQa="District"
                            />
                            <Button variant="outlined" onClick={handleClose}>
                                Cancelar
                            </Button>
                        </Stack>
                    </form>
                </Box>
            </Modal>
        </div>
    );
}

export default Form;
